/**
 * Control Portal - Plan Builder
 * Create and manage subscription plan templates
 */
import { useState, useEffect } from 'react';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Shield, Plus, Edit2, Trash2, Loader2, RefreshCw,
  MapPin, Thermometer, Weight, Check, X
} from 'lucide-react';

export default function PlanBuilderPage() {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    allow_gps: true,
    allow_temp: true,
    allow_weight: false,
    default_license_days: 365,
    default_grace_days: 7,
    default_telemetry_interval: 30,
    price_monthly: '',
    price_yearly: ''
  });

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await controlApi.getPlans({ active_only: false });
      setPlans(res.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const openCreateDialog = () => {
    setEditingPlan(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      allow_gps: true,
      allow_temp: true,
      allow_weight: false,
      default_license_days: 365,
      default_grace_days: 7,
      default_telemetry_interval: 30,
      price_monthly: '',
      price_yearly: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (plan) => {
    setEditingPlan(plan);
    setFormData({
      code: plan.code,
      name: plan.name,
      description: plan.description || '',
      allow_gps: plan.allow_gps,
      allow_temp: plan.allow_temp,
      allow_weight: plan.allow_weight,
      default_license_days: plan.default_license_days,
      default_grace_days: plan.default_grace_days,
      default_telemetry_interval: plan.default_telemetry_interval,
      price_monthly: plan.price_monthly || '',
      price_yearly: plan.price_yearly || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name) {
      toast.error('Code and name are required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        price_monthly: formData.price_monthly ? parseInt(formData.price_monthly) : null,
        price_yearly: formData.price_yearly ? parseInt(formData.price_yearly) : null
      };
      
      if (editingPlan) {
        await controlApi.updatePlan(editingPlan.id, payload);
        toast.success('Plan updated');
      } else {
        await controlApi.createPlan(payload);
        toast.success('Plan created');
      }
      setDialogOpen(false);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!confirm(`Deactivate plan "${plan.name}"?`)) return;
    try {
      await controlApi.deletePlan(plan.id);
      toast.success('Plan deactivated');
      fetchPlans();
    } catch (error) {
      toast.error('Failed to deactivate plan');
    }
  };

  const getFeatureBadges = (plan) => {
    const badges = [];
    if (plan.allow_gps) badges.push({ icon: MapPin, label: 'GPS', color: 'blue' });
    if (plan.allow_temp) badges.push({ icon: Thermometer, label: 'TEMP', color: 'amber' });
    if (plan.allow_weight) badges.push({ icon: Weight, label: 'WEIGHT', color: 'green' });
    return badges;
  };

  return (
    <div className="p-6" data-testid="plan-builder-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-500" />
            Subscription Plans
          </h1>
          <p className="text-slate-400 mt-1">Create and manage feature plan templates</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPlans} className="border-slate-700 text-slate-300">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Create Plan
          </Button>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {plans.filter(p => p.is_active).map((plan) => (
          <div
            key={plan.id}
            className="bg-slate-900 rounded-xl border border-slate-800 p-5 hover:border-slate-700 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <Badge variant="outline" className="border-blue-500 text-blue-400 mb-2">
                  {plan.code}
                </Badge>
                <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                {plan.description && (
                  <p className="text-slate-400 text-sm mt-1">{plan.description}</p>
                )}
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openEditDialog(plan)}
                  className="h-8 w-8 text-slate-400 hover:text-blue-400"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(plan)}
                  className="h-8 w-8 text-slate-400 hover:text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Feature Badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {getFeatureBadges(plan).map((badge, i) => {
                const Icon = badge.icon;
                return (
                  <Badge
                    key={i}
                    className={`bg-${badge.color}-500/20 text-${badge.color}-400 border-${badge.color}-500/30`}
                  >
                    <Icon className="w-3 h-3 mr-1" />
                    {badge.label}
                  </Badge>
                );
              })}
              {!plan.allow_gps && !plan.allow_temp && !plan.allow_weight && (
                <Badge variant="secondary">No Features</Badge>
              )}
            </div>
            
            {/* Settings */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-slate-400">License: <span className="text-white">{plan.default_license_days}d</span></div>
              <div className="text-slate-400">Grace: <span className="text-white">{plan.default_grace_days}d</span></div>
              <div className="text-slate-400">Interval: <span className="text-white">{plan.default_telemetry_interval}s</span></div>
              {plan.price_monthly && (
                <div className="text-slate-400">Price: <span className="text-green-400">₹{(plan.price_monthly / 100).toFixed(0)}/mo</span></div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Inactive Plans */}
      {plans.filter(p => !p.is_active).length > 0 && (
        <div className="bg-slate-900/50 rounded-xl border border-slate-800 p-4">
          <h3 className="text-slate-400 font-semibold mb-3">Inactive Plans</h3>
          <div className="flex flex-wrap gap-2">
            {plans.filter(p => !p.is_active).map((plan) => (
              <Badge key={plan.id} variant="secondary" className="opacity-50">
                {plan.code} - {plan.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingPlan ? 'Edit Plan' : 'Create Plan'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Define feature toggles and default settings for this plan
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Plan Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="GPS_TEMP"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  disabled={!!editingPlan}
                />
              </div>
              <div>
                <Label className="text-slate-300">Plan Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="GPS + Temperature"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Standard cold chain monitoring"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            
            {/* Feature Toggles */}
            <div className="bg-slate-800 rounded-lg p-4">
              <h4 className="text-white font-medium mb-3">Feature Toggles</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-400" />
                    <span className="text-white">GPS Tracking</span>
                  </div>
                  <Switch
                    checked={formData.allow_gps}
                    onCheckedChange={(v) => setFormData({ ...formData, allow_gps: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Thermometer className="w-4 h-4 text-amber-400" />
                    <span className="text-white">Temperature Monitoring</span>
                  </div>
                  <Switch
                    checked={formData.allow_temp}
                    onCheckedChange={(v) => setFormData({ ...formData, allow_temp: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Weight className="w-4 h-4 text-green-400" />
                    <span className="text-white">Weight Monitoring</span>
                  </div>
                  <Switch
                    checked={formData.allow_weight}
                    onCheckedChange={(v) => setFormData({ ...formData, allow_weight: v })}
                  />
                </div>
              </div>
            </div>
            
            {/* Default Settings */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300">License Days</Label>
                <Input
                  type="number"
                  value={formData.default_license_days}
                  onChange={(e) => setFormData({ ...formData, default_license_days: parseInt(e.target.value) || 0 })}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Grace Days</Label>
                <Input
                  type="number"
                  value={formData.default_grace_days}
                  onChange={(e) => setFormData({ ...formData, default_grace_days: parseInt(e.target.value) || 0 })}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Interval (sec)</Label>
                <Input
                  type="number"
                  value={formData.default_telemetry_interval}
                  onChange={(e) => setFormData({ ...formData, default_telemetry_interval: parseInt(e.target.value) || 30 })}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>
            
            {/* Pricing (optional) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Monthly Price (paise)</Label>
                <Input
                  type="number"
                  value={formData.price_monthly}
                  onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
                  placeholder="Optional"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Yearly Price (paise)</Label>
                <Input
                  type="number"
                  value={formData.price_yearly}
                  onChange={(e) => setFormData({ ...formData, price_yearly: e.target.value })}
                  placeholder="Optional"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button variant="ghost" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingPlan ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
